import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CalendarClock,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ListPlus,
  Loader2,
  Pencil,
  Plus,
  Square,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useBusiness } from '../../contexts/BusinessContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { useTodoViewer } from '../../hooks/useTodoViewer';
import {
  completeTodoItem,
  countDueTodoItems,
  createTodoItem,
  createTodoList,
  deleteTodoItem,
  findTodoItems,
  getTodoListMembers,
  getTodoLists,
  invalidateTodoListsCache,
  isTodoOverdue,
  reopenTodoItem,
  setTodoListMembers,
  updateTodoItem,
  updateTodoList,
  type TodoItemWithRelations,
  type TodoList,
  type TodoViewer,
} from '../../lib/todos';
import { findEventsForTodos, type CalendarEventRow } from '../../lib/calendarEvents';
import { getResources, type Resource } from '../../lib/resources';
import SelectCustomerModal from '../manage/SelectCustomerModal';
import CustomerDetailsModal from '../CustomerDetailsModal';
import TodoTaskCustomerCard from './TodoTaskCustomerCard';
import type { Customer } from '../../lib/customers';

type FilterTab = 'open' | 'completed';
type View = 'list' | 'create' | 'create-list' | 'edit' | 'edit-list';

function formatRequiredTime(minutes: number | null | undefined): string | null {
  if (!minutes || minutes <= 0) return null;
  if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60}h`;
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function hoursFromMinutes(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '';
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : String(Math.round(hours * 100) / 100);
}

function minutesFromHoursInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const hours = Number(trimmed);
  if (!Number.isFinite(hours) || hours <= 0) return null;
  return Math.round(hours * 60);
}

function customerName(customer: TodoItemWithRelations['customer'] | Customer | null | undefined) {
  if (!customer) return null;
  const name = `${(customer as any).first_name || ''} ${(customer as any).last_name || ''}`.trim();
  return name || null;
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return fallback;
}

function assigneeName(assignee: TodoItemWithRelations['assignee'] | Resource | null | undefined) {
  if (!assignee) return null;
  const name = `${(assignee as any).first_name || ''} ${(assignee as any).last_name || ''}`.trim();
  return name || (assignee as any).name || null;
}

function formatDue(item: TodoItemWithRelations): string | null {
  if (!item.due_date) return null;
  try {
    const dateLabel = format(parseISO(item.due_date), 'MMM d, yyyy');
    if (item.due_time) {
      const [hh, mm] = item.due_time.slice(0, 5).split(':').map(Number);
      const d = new Date();
      d.setHours(hh, mm, 0, 0);
      return `${dateLabel} · ${format(d, 'h:mm a')}`;
    }
    return dateLabel;
  } catch {
    return item.due_date;
  }
}

// Time-aware: red only once the task's due moment has actually passed
// (past days always; today only after the due time). Shared with the host.
const isOverdue = (item: TodoItemWithRelations): boolean => isTodoOverdue(item);

function sortTodoItems(items: TodoItemWithRelations[]): TodoItemWithRelations[] {
  return [...items].sort((a, b) => {
    const aOver = isOverdue(a) ? 0 : 1;
    const bOver = isOverdue(b) ? 0 : 1;
    if (aOver !== bOver) return aOver - bOver;
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });
}

interface ControlCentreTasksBadgeProps {
  className?: string;
  /** Banner chip on Control Centre vs icon-only in main top nav */
  variant?: 'banner' | 'nav';
}

/** Opens Control Centre Tasks — banner chip or main-nav icon. */
export default function ControlCentreTasksBadge({
  className = '',
  variant = 'banner',
}: ControlCentreTasksBadgeProps) {
  const { selectedBusiness } = useBusiness();
  const viewer = useTodoViewer();
  const [dueCount, setDueCount] = useState<number | null>(null);

  const refreshCount = useCallback(async () => {
    if (!selectedBusiness?.id || !viewer) {
      setDueCount(null);
      return;
    }
    try {
      // Personal count: the viewer's visible tasks due today or earlier.
      setDueCount(await countDueTodoItems(selectedBusiness.id, viewer));
    } catch {
      setDueCount(null);
    }
  }, [selectedBusiness?.id, viewer]);

  useEffect(() => {
    void refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    const onTodosChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ businessId?: string }>).detail;
      if (detail?.businessId && selectedBusiness?.id && detail.businessId !== selectedBusiness.id) {
        return;
      }
      void refreshCount();
    };
    window.addEventListener('todoItemsChanged', onTodosChanged);
    return () => window.removeEventListener('todoItemsChanged', onTodosChanged);
  }, [refreshCount, selectedBusiness?.id]);

  const countLabel =
    dueCount == null || dueCount <= 0 ? null : dueCount > 99 ? '99+' : String(dueCount);

  const openTasks = () => {
    window.dispatchEvent(new CustomEvent('assistant:openTodos'));
  };

  if (variant === 'nav') {
    return (
      <button
        type="button"
        onClick={openTasks}
        className={`relative p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors touch-manipulation ${className}`}
        title="Tasks"
        aria-label={
          dueCount != null && dueCount > 0
            ? `Tasks · ${dueCount} due. Open tasks`
            : 'Tasks. Open tasks'
        }
        data-assistant-anchor="tasks-button"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <CheckSquare className="h-5 w-5" />
        {countLabel != null ? (
          <span className="absolute top-0 right-0 min-w-[1rem] rounded-full bg-amber-400 px-1 py-0.5 text-center text-[10px] font-semibold leading-none tabular-nums text-gray-900 shadow-sm">
            {countLabel}
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={openTasks}
      className={`inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 backdrop-blur-sm hover:bg-white/10 active:scale-[0.98] transition-all ${className}`}
      title="Tasks"
      aria-label={
        dueCount != null && dueCount > 0
          ? `Tasks · ${dueCount} due. Open tasks`
          : 'Tasks. Open tasks'
      }
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-emerald-300">
        <CheckSquare className="h-3.5 w-3.5" />
      </span>
      <span className="text-[13px] font-semibold text-white leading-none">Tasks</span>
      {countLabel != null ? (
        <span className="min-w-[1.25rem] rounded-full bg-amber-400/25 px-1.5 py-0.5 text-center text-[11px] font-semibold tabular-nums text-amber-100 leading-none">
          {countLabel}
        </span>
      ) : null}
    </button>
  );
}

interface ControlCentreTasksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChanged?: () => void;
  /** When set (e.g. from a focused list pencil), open straight on the edit form */
  editTodoId?: string | null;
  onEditTodoIdConsumed?: () => void;
}

export function ControlCentreTasksModal({
  isOpen,
  onClose,
  onChanged,
  editTodoId = null,
  onEditTodoIdConsumed,
}: ControlCentreTasksModalProps) {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissionCheck();
  const ownViewer = useTodoViewer();
  const businessId = selectedBusiness?.id;

  // Admins can browse any resource's task world ("Viewing" dropdown).
  const [viewAsResourceId, setViewAsResourceId] = useState<string | null>(null);
  const viewer: TodoViewer | null = useMemo(() => {
    if (!ownViewer) return null;
    if (ownViewer.isAdmin && viewAsResourceId) {
      return { resourceId: viewAsResourceId, isAdmin: false };
    }
    return ownViewer;
  }, [ownViewer, viewAsResourceId]);

  const [view, setView] = useState<View>('list');
  const [filter, setFilter] = useState<FilterTab>('open');
  const [lists, setLists] = useState<TodoList[]>([]);
  const [items, setItems] = useState<TodoItemWithRelations[]>([]);
  const [dueItems, setDueItems] = useState<TodoItemWithRelations[]>([]);
  const [linkedEvents, setLinkedEvents] = useState<Map<string, CalendarEventRow>>(new Map());
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedListIds, setExpandedListIds] = useState<Set<string>>(new Set());
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  // Create / edit form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [listId, setListId] = useState<string>('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [requiredHours, setRequiredHours] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [shareWithIds, setShareWithIds] = useState<Set<string>>(new Set());
  const [editingList, setEditingList] = useState<TodoList | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [customerForDetails, setCustomerForDetails] = useState<Customer | null>(null);
  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const inboxList = useMemo(
    () => lists.find((l) => l.is_default) || lists[0] || null,
    [lists]
  );
  const otherLists = useMemo(
    () => lists.filter((l) => !l.is_default && l.id !== inboxList?.id),
    [lists, inboxList?.id]
  );

  const resetCreateForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setDueDate('');
    setDueTime('');
    setAssigneeId('');
    setRequiredHours('');
    setCustomer(null);
    setNewListName('');
    setCreatingList(false);
    setEditingItemId(null);
    setShareWithIds(new Set());
    setEditingList(null);
    setError(null);
  }, []);

  const fillFormFromItem = useCallback((item: TodoItemWithRelations) => {
    setEditingItemId(item.id);
    setTitle(item.title || '');
    setDescription(item.description || '');
    setListId(item.list_id);
    setDueDate(item.due_date || '');
    setDueTime(item.due_time ? String(item.due_time).slice(0, 5) : '');
    setAssigneeId(item.assignee_resource_id || '');
    setRequiredHours(hoursFromMinutes(item.estimated_minutes));
    if (item.customer) {
      setCustomer({
        id: item.customer.id,
        first_name: item.customer.first_name,
        last_name: item.customer.last_name,
        email: item.customer.email,
        phone: item.customer.phone,
      } as Customer);
    } else {
      setCustomer(null);
    }
    setError(null);
  }, []);

  const openEditTask = useCallback(
    (item: TodoItemWithRelations) => {
      fillFormFromItem(item);
      setView('edit');
    },
    [fillFormFromItem]
  );

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    handleClose();
  };

  const handleTransitionEnd = () => {
    if (!isOpen) setIsAnimating(false);
  };

  const load = useCallback(async () => {
    if (!businessId || !viewer) return;
    setLoading(true);
    setError(null);
    try {
      const openFilter = filter === 'open';
      const [listRows, itemRows, dueRows, team] = await Promise.all([
        getTodoLists(businessId, { viewer }),
        findTodoItems(businessId, {
          status: openFilter ? 'open' : 'completed',
          visible_to: viewer,
          limit: 200,
        }),
        openFilter
          ? findTodoItems(businessId, { actually_due: true, visible_to: viewer, limit: 200 })
          : Promise.resolve([] as TodoItemWithRelations[]),
        getResources(businessId).catch(() => [] as Resource[]),
      ]);
      setLists(listRows);
      setItems(itemRows);
      setDueItems(sortTodoItems(dueRows));
      setResources(team.filter((r) => r.status === 'active'));
      try {
        const eventTodoIds = [
          ...new Set([...itemRows, ...dueRows].map((item) => item.id)),
        ];
        const linked = await findEventsForTodos(businessId, eventTodoIds);
        setLinkedEvents(linked);
      } catch {
        setLinkedEvents(new Map());
      }

      const inbox = listRows.find((l) => l.is_default) || listRows[0];
      if (inbox) {
        setListId((prev) => prev || inbox.id);
        setExpandedListIds((prev) => {
          if (prev.size > 0) return prev;
          return new Set([inbox.id]);
        });
      }
    } catch (err) {
      setError(errorMessage(err, 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
  }, [businessId, filter, viewer]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setView('list');
      setFilter('open');
      setViewAsResourceId(null);
      setExpandedListIds(new Set());
      resetCreateForm();
      document.body.style.overflow = 'hidden';
      void load();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional open reset

  // Open straight into edit when a todo id is passed in (e.g. from focused list pencil)
  useEffect(() => {
    if (!isOpen || !editTodoId || !businessId) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await findTodoItems(businessId, {
          status: 'all',
          visible_to: viewer || undefined,
          limit: 200,
        });
        if (cancelled) return;
        const item = rows.find((r) => r.id === editTodoId);
        if (item) {
          fillFormFromItem(item);
          setView('edit');
          if (item.list_id) {
            setExpandedListIds((prev) => new Set([...prev, item.list_id]));
          }
        }
      } catch {
        /* ignore — list load surfaces errors */
      } finally {
        onEditTodoIdConsumed?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, editTodoId, businessId, viewer, fillFormFromItem, onEditTodoIdConsumed]);

  useEffect(() => {
    if (!isOpen) return;
    void load();
  }, [filter, isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onTodosChanged = () => {
      void load();
    };
    window.addEventListener('todoItemsChanged', onTodosChanged);
    return () => window.removeEventListener('todoItemsChanged', onTodosChanged);
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (customerDetailsOpen) {
        setCustomerDetailsOpen(false);
        setCustomerForDetails(null);
        return;
      }
      if (customerPickerOpen) {
        setCustomerPickerOpen(false);
        return;
      }
      if (view !== 'list') {
        setView('list');
        resetCreateForm();
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, resetCreateForm, view, customerDetailsOpen, customerPickerOpen]);

  const visibleListIds = useMemo(() => new Set(lists.map((l) => l.id)), [lists]);

  // Tasks assigned to the viewer that live in lists they can't see roll into
  // the Inbox section as an "Assigned to you" group.
  const assignedElsewhere = useMemo(
    () => sortTodoItems(items.filter((i) => !visibleListIds.has(i.list_id))),
    [items, visibleListIds]
  );

  const itemsByListId = useMemo(() => {
    const map = new Map<string, TodoItemWithRelations[]>();
    for (const item of items) {
      const key = item.list_id;
      if (!visibleListIds.has(key)) continue;
      const bucket = map.get(key) || [];
      bucket.push(item);
      map.set(key, bucket);
    }
    for (const [key, bucket] of map) {
      bucket.sort((a, b) => {
        const aOver = isOverdue(a) ? 0 : 1;
        const bOver = isOverdue(b) ? 0 : 1;
        if (aOver !== bOver) return aOver - bOver;
        if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
        if (a.due_date) return -1;
        if (b.due_date) return 1;
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
      map.set(key, bucket);
    }
    return map;
  }, [items, visibleListIds]);

  const toggleListExpanded = (id: string) => {
    setExpandedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreateTask = (preferredListId?: string) => {
    const target =
      preferredListId ||
      [...expandedListIds].find((id) => id !== inboxList?.id) ||
      inboxList?.id ||
      listId;
    resetCreateForm();
    if (target) setListId(target);
    // Resources (and admins browsing a resource's world) default to that person.
    if (viewer && !viewer.isAdmin && viewer.resourceId) {
      setAssigneeId(viewer.resourceId);
    }
    setView('create');
  };

  const handleToggle = async (item: TodoItemWithRelations) => {
    if (!businessId || togglingId) return;
    setTogglingId(item.id);
    try {
      if (item.status === 'open') {
        await completeTodoItem(businessId, item.id);
      } else {
        await reopenTodoItem(businessId, item.id);
      }
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, 'Failed to update task'));
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (item: TodoItemWithRelations) => {
    if (!businessId || deletingId) return;
    if (!window.confirm(`Delete task "${item.title}"?`)) return;
    setDeletingId(item.id);
    try {
      await deleteTodoItem(businessId, item.id);
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, 'Failed to delete task'));
    } finally {
      setDeletingId(null);
    }
  };

  // Whose world new lists/tasks belong to: the viewed resource when an admin
  // is browsing someone else's tasks, otherwise the current user.
  const creatorResourceId = viewAsResourceId || currentResource?.id || null;

  const toggleShareWith = (resourceId: string) => {
    setShareWithIds((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  };

  const handleCreateList = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    if (!businessId || !newListName.trim() || creatingList) return;
    setCreatingList(true);
    setError(null);
    try {
      const list = await createTodoList(businessId, newListName.trim(), {
        created_by_resource_id: creatorResourceId,
      });
      if (shareWithIds.size > 0) {
        await setTodoListMembers(businessId, list.id, [...shareWithIds], currentResource?.id || null);
      }
      invalidateTodoListsCache(businessId);
      setLists((prev) => [...prev, list]);
      setListId(list.id);
      setExpandedListIds((prev) => new Set([...prev, list.id]));
      setNewListName('');
      setShareWithIds(new Set());
      setView('list');
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, 'Failed to create list'));
    } finally {
      setCreatingList(false);
    }
  };

  const openEditList = async (list: TodoList) => {
    if (!businessId) return;
    setEditingList(list);
    setNewListName(list.name);
    setShareWithIds(new Set());
    setError(null);
    setView('edit-list');
    setMembersLoading(true);
    try {
      const members = await getTodoListMembers(businessId, list.id);
      setShareWithIds(new Set(members.map((m) => m.resource_id)));
    } catch {
      /* members list starts empty — saving still works */
    } finally {
      setMembersLoading(false);
    }
  };

  const handleSaveList = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    if (!businessId || !editingList || !newListName.trim() || creatingList) return;
    setCreatingList(true);
    setError(null);
    try {
      if (newListName.trim() !== editingList.name) {
        await updateTodoList(businessId, editingList.id, { name: newListName.trim() });
      }
      await setTodoListMembers(businessId, editingList.id, [...shareWithIds], currentResource?.id || null);
      invalidateTodoListsCache(businessId);
      setEditingList(null);
      setNewListName('');
      setShareWithIds(new Set());
      setView('list');
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, 'Failed to save list'));
    } finally {
      setCreatingList(false);
    }
  };

  const handleSaveTask = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    if (!businessId || !title.trim() || saving) return;

    const estimatedMinutes = minutesFromHoursInput(requiredHours);
    if (requiredHours.trim() && estimatedMinutes == null) {
      setError('Required time must be a positive number of hours (e.g. 3 or 1.5)');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (view === 'edit' && editingItemId) {
        await updateTodoItem(businessId, editingItemId, {
          title,
          description: description.trim() || null,
          list_id: listId || undefined,
          due_date: dueDate || null,
          due_time: dueDate && dueTime ? dueTime : null,
          assignee_resource_id: assigneeId || null,
          customer_id: customer?.id || null,
          estimated_minutes: estimatedMinutes,
        });
        const savedListId = listId;
        resetCreateForm();
        setView('list');
        if (savedListId) {
          setExpandedListIds((prev) => new Set([...prev, savedListId]));
        }
      } else {
        const created = await createTodoItem(
          businessId,
          {
            title,
            description: description.trim() || null,
            list_id: listId || null,
            due_date: dueDate || null,
            due_time: dueDate && dueTime ? dueTime : null,
            assignee_resource_id: assigneeId || null,
            customer_id: customer?.id || null,
            estimated_minutes: estimatedMinutes,
            created_by_resource_id: creatorResourceId,
          },
          viewer || undefined
        );
        resetCreateForm();
        setView('list');
        setFilter('open');
        if (created.list_id) {
          setExpandedListIds((prev) => new Set([...prev, created.list_id]));
        }
      }
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, view === 'edit' ? 'Failed to save task' : 'Failed to create task'));
    } finally {
      setSaving(false);
    }
  };

  const renderTaskRow = (item: TodoItemWithRelations, opts?: { showListName?: boolean }) => {
    const due = formatDue(item);
    const overdue = isOverdue(item);
    const busy = togglingId === item.id;
    const required = formatRequiredTime(item.estimated_minutes);
    const linked = linkedEvents.get(item.id);
    const listLabel =
      opts?.showListName && item.list && !item.list.is_default ? item.list.name : null;
    const scheduledLabel = linked
      ? (() => {
          try {
            const dateLabel = format(parseISO(linked.start_date), 'MMM d');
            return `Scheduled ${dateLabel} · ${String(linked.start_time).slice(0, 5)}`;
          } catch {
            return `Scheduled ${linked.start_date}`;
          }
        })()
      : null;
    return (
      <div key={item.id} className="flex items-start gap-2 rounded-xl px-2 py-2.5 hover:bg-gray-50 group">
        <button
          type="button"
          onClick={() => void handleToggle(item)}
          disabled={busy}
          className="mt-0.5 shrink-0 text-gray-400 hover:text-emerald-600 disabled:opacity-50"
          aria-label={item.status === 'open' ? 'Mark complete' : 'Reopen task'}
        >
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : item.status === 'completed' ? (
            <Check className="h-5 w-5 text-emerald-600" />
          ) : (
            <Square className="h-5 w-5" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <p
            className={`text-sm font-medium leading-snug ${
              item.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'
            }`}
          >
            {item.title}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
            {due ? (
              <span className={overdue ? 'text-red-600 font-medium' : ''}>
                {overdue ? `Overdue · ${due}` : due}
              </span>
            ) : null}
            {listLabel ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 font-medium px-2 py-0.5">
                {listLabel}
              </span>
            ) : null}
            {required ? <span className="font-medium text-gray-600">{required}</span> : null}
            {scheduledLabel ? (
              <span className="text-emerald-700 font-medium">{scheduledLabel}</span>
            ) : null}
            {assigneeName(item.assignee) ? <span>{assigneeName(item.assignee)}</span> : null}
          </div>
          {item.description ? (
            <p className="mt-1 text-xs text-gray-500 line-clamp-2">{item.description}</p>
          ) : null}
          {item.customer ? (
            <TodoTaskCustomerCard
              customer={item.customer}
              businessId={businessId}
              onOpenCustomer={(customer) => {
                setCustomerForDetails(customer);
                setCustomerDetailsOpen(true);
              }}
            />
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => openEditTask(item)}
          className="mt-0.5 shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          aria-label={`Edit ${item.title}`}
          title="Edit task"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => void handleDelete(item)}
          disabled={deletingId === item.id}
          className="mt-0.5 shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
          aria-label={`Delete ${item.title}`}
          title="Delete task"
        >
          {deletingId === item.id ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
    );
  };

  const renderDueSection = () => {
    const count = dueItems.length;

    return (
      <section className="rounded-xl border border-amber-200/80 overflow-hidden bg-white">
        <div className="flex items-center gap-1 px-2 py-2 bg-amber-50/80">
          <div className="flex-1 min-w-0 flex items-center gap-2 px-1 py-1.5">
            <CalendarClock className="h-4 w-4 text-amber-600 shrink-0" />
            <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">Today</span>
            <span className="text-xs font-semibold tabular-nums text-amber-800 bg-amber-100 rounded-full px-2 py-0.5">
              {count}
            </span>
          </div>
        </div>

        <div className="border-t border-amber-100/80 px-1 py-1">
          {count === 0 ? (
            <p className="px-3 py-4 text-xs text-gray-500">Nothing due today or overdue</p>
          ) : (
            <ul>{dueItems.map((item) => renderTaskRow(item, { showListName: true }))}</ul>
          )}
        </div>
      </section>
    );
  };

  const renderListSection = (
    list: TodoList,
    opts?: { alwaysExpanded?: boolean; assignedItems?: TodoItemWithRelations[] }
  ) => {
    const listItems = itemsByListId.get(list.id) || [];
    const expanded = opts?.alwaysExpanded || expandedListIds.has(list.id);
    const assigned = opts?.assignedItems || [];
    const count = listItems.length + assigned.length;
    const canEditList =
      !list.is_default &&
      (ownViewer?.isAdmin || (viewer?.resourceId && list.created_by_resource_id === viewer.resourceId));

    return (
      <section key={list.id} className="rounded-xl border border-gray-200 overflow-hidden bg-white">
        <div className={`flex items-center gap-1 px-2 py-2 ${opts?.alwaysExpanded ? 'bg-gray-50' : ''}`}>
          <button
            type="button"
            onClick={() => {
              if (opts?.alwaysExpanded) return;
              toggleListExpanded(list.id);
            }}
            className={`flex-1 min-w-0 flex items-center gap-2 px-1 py-1.5 text-left rounded-lg transition-colors ${
              opts?.alwaysExpanded ? 'cursor-default' : 'hover:bg-gray-100'
            }`}
            aria-expanded={expanded}
          >
            {!opts?.alwaysExpanded ? (
              expanded ? (
                <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
              )
            ) : (
              <CheckSquare className="h-4 w-4 text-emerald-600 shrink-0" />
            )}
            <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
              {list.name}
            </span>
            <span className="text-xs font-semibold tabular-nums text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
              {count}
            </span>
          </button>
          {canEditList ? (
            <button
              type="button"
              onClick={() => void openEditList(list)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"
              aria-label={`Share or rename ${list.name}`}
              title="Share / rename list"
            >
              <Users className="h-4 w-4" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => openCreateTask(list.id)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 shrink-0"
            aria-label={`Add task to ${list.name}`}
            title="Add task"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {expanded ? (
          <div className="border-t border-gray-100 px-1 py-1">
            {listItems.length === 0 && assigned.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-500">
                {filter === 'open' ? 'No open tasks in this list' : 'No completed tasks here'}
              </p>
            ) : (
              <>
                {listItems.length > 0 ? <ul>{listItems.map(renderTaskRow)}</ul> : null}
                {assigned.length > 0 ? (
                  <div className={listItems.length > 0 ? 'mt-1 border-t border-gray-100 pt-1' : ''}>
                    <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      Assigned to you
                    </p>
                    <ul>{assigned.map((item) => renderTaskRow(item, { showListName: true }))}</ul>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </section>
    );
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <>
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${
          isOpen ? 'bg-black/50' : 'bg-black/0'
        }`}
        style={{
          zIndex: 99999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
        }}
        onClick={handleBackdropClick}
        onTransitionEnd={handleTransitionEnd}
        role="dialog"
        aria-modal="true"
        aria-labelledby="control-centre-tasks-title"
      >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 99999,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 shrink-0 relative">
            <button
              type="button"
              onClick={
                view !== 'list'
                  ? () => {
                      setView('list');
                      resetCreateForm();
                    }
                  : onClose
              }
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label={view === 'list' ? 'Close' : 'Back'}
            >
              <X className="h-5 w-5" />
            </button>

            <div className="pr-10 flex items-start gap-2">
              {view !== 'list' ? (
                <button
                  type="button"
                  onClick={() => {
                    setView('list');
                    resetCreateForm();
                  }}
                  className="mt-1 p-1.5 -ml-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label="Back to tasks"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              ) : null}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2
                      id="control-centre-tasks-title"
                      className="text-2xl font-bold text-gray-900"
                    >
                      {view === 'create'
                        ? 'New task'
                        : view === 'edit'
                          ? 'Edit task'
                          : view === 'create-list'
                            ? 'New list'
                            : view === 'edit-list'
                              ? 'Edit list'
                              : 'Tasks'}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {view === 'create'
                        ? 'Title is enough — everything else is optional'
                        : view === 'edit'
                          ? 'Update details, due date, or required time'
                          : view === 'create-list'
                            ? 'Organize tasks into custom lists'
                            : view === 'edit-list'
                              ? 'Rename the list or change who it is shared with'
                              : 'To-dos, reminders, and follow-ups'}
                    </p>
                  </div>
                  {view === 'list' ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setView('create-list')}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        <ListPlus className="h-4 w-4" />
                        List
                      </button>
                      <button
                        type="button"
                        onClick={() => openCreateTask()}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {view === 'list' ? (
            <>
              <div className="px-6 pt-4 pb-2 shrink-0 flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                  {(['open', 'completed'] as FilterTab[]).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setFilter(tab)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md capitalize transition-colors ${
                        filter === tab
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                {ownViewer?.isAdmin ? (
                  <label className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                    <span className="font-medium">Viewing</span>
                    <select
                      value={viewAsResourceId || ''}
                      onChange={(e) => {
                        setViewAsResourceId(e.target.value || null);
                        setExpandedListIds(new Set());
                      }}
                      className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-semibold text-gray-800 outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Whose tasks to view"
                    >
                      <option value="">My Tasks</option>
                      {resources
                        .filter((r) => r.id !== ownViewer.resourceId)
                        .map((r) => (
                          <option key={r.id} value={r.id}>
                            {assigneeName(r) || 'Team member'}
                          </option>
                        ))}
                    </select>
                  </label>
                ) : null}
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6 min-h-0 space-y-3">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
                  </div>
                ) : (
                  <>
                    {filter === 'open' ? renderDueSection() : null}

                    {inboxList
                      ? renderListSection(inboxList, {
                          alwaysExpanded: true,
                          assignedItems: assignedElsewhere,
                        })
                      : null}

                    {otherLists.length > 0 ? (
                      <div className="space-y-2">
                        <p className="px-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                          Lists
                        </p>
                        {otherLists.map((list) => renderListSection(list))}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setView('create-list')}
                        className="w-full rounded-xl border border-dashed border-gray-300 px-4 py-4 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50"
                      >
                        Create a list like To Do or Long-term
                      </button>
                    )}
                  </>
                )}
              </div>
            </>
          ) : view === 'create-list' || view === 'edit-list' ? (
            <form
              onSubmit={(e) =>
                view === 'edit-list' ? void handleSaveList(e) : void handleCreateList(e)
              }
              className="flex-1 overflow-y-auto px-6 py-6 space-y-4 min-h-0"
            >
              <div>
                <label htmlFor="todo-list-name" className="block text-sm font-medium text-gray-700 mb-2">
                  List name <span className="text-red-500">*</span>
                </label>
                <input
                  id="todo-list-name"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g. To Do, Long-term improvements"
                  autoFocus
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Share with</span>
                <p className="text-xs text-gray-500 mb-2">
                  People you add can see this list and every task in it.
                </p>
                {membersLoading ? (
                  <div className="flex items-center gap-2 px-1 py-2 text-xs text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading members…
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                    {resources
                      .filter((r) => {
                        const ownerId =
                          view === 'edit-list'
                            ? editingList?.created_by_resource_id
                            : creatorResourceId;
                        return r.id !== ownerId;
                      })
                      .map((r) => (
                        <label
                          key={r.id}
                          className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-800 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={shareWithIds.has(r.id)}
                            onChange={() => toggleShareWith(r.id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="truncate">{assigneeName(r) || 'Team member'}</span>
                        </label>
                      ))}
                    {resources.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-gray-500">
                        No active team members to share with yet.
                      </p>
                    ) : null}
                  </div>
                )}
              </div>

              {error ? (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
              ) : null}
              <button
                type="submit"
                disabled={!newListName.trim() || creatingList || membersLoading}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {creatingList
                  ? view === 'edit-list'
                    ? 'Saving…'
                    : 'Creating…'
                  : view === 'edit-list'
                    ? 'Save list'
                    : 'Create list'}
              </button>
            </form>
          ) : (
            <form
              onSubmit={(e) => void handleSaveTask(e)}
              className="flex-1 overflow-y-auto px-6 py-6 space-y-4 min-h-0"
            >
              <div>
                <label htmlFor="todo-title" className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="todo-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Follow up with…"
                  autoFocus
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="todo-description" className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  id="todo-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Optional details"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                />
              </div>

              <div>
                <label htmlFor="todo-list" className="block text-sm font-medium text-gray-700 mb-2">
                  List
                </label>
                <select
                  id="todo-list"
                  value={listId}
                  onChange={(e) => setListId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                >
                  {lists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                      {list.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="todo-due-date" className="block text-sm font-medium text-gray-700 mb-2">
                    Due date
                  </label>
                  <input
                    id="todo-due-date"
                    type="date"
                    value={dueDate}
                    onChange={(e) => {
                      setDueDate(e.target.value);
                      if (!e.target.value) setDueTime('');
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="todo-due-time" className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <input
                    id="todo-due-time"
                    type="time"
                    value={dueTime}
                    disabled={!dueDate}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="todo-required-hours" className="block text-sm font-medium text-gray-700 mb-2">
                  Required time
                </label>
                <div className="relative">
                  <input
                    id="todo-required-hours"
                    type="number"
                    min="0.25"
                    step="0.25"
                    inputMode="decimal"
                    value={requiredHours}
                    onChange={(e) => setRequiredHours(e.target.value)}
                    placeholder="e.g. 3"
                    className="w-full px-4 py-2.5 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                    hours
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Optional — used when blocking calendar time for this task
                </p>
              </div>

              <div>
                <label htmlFor="todo-assignee" className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to
                </label>
                <select
                  id="todo-assignee"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                >
                  <option value="">Unassigned</option>
                  {resources.map((r) => (
                    <option key={r.id} value={r.id}>
                      {assigneeName(r) || 'Team member'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
                {customer ? (
                  <div className="flex items-center justify-between rounded-lg border border-gray-300 px-4 py-2.5">
                    <span className="text-sm text-gray-900 truncate">
                      {customerName(customer)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCustomer(null)}
                      className="text-sm font-medium text-gray-500 hover:text-gray-800"
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCustomerPickerOpen(true)}
                    className="w-full rounded-lg border-2 border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 text-left"
                  >
                    Link a customer (optional)
                  </button>
                )}
              </div>

              {error ? (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
              ) : null}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={!title.trim() || saving}
                  className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving
                    ? view === 'edit'
                      ? 'Saving…'
                      : 'Creating…'
                    : view === 'edit'
                      ? 'Save changes'
                      : 'Create task'}
                </button>
              </div>
            </form>
          )}

          {view === 'list' && error ? (
            <div className="px-6 pb-4 shrink-0">
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
            </div>
          ) : null}
        </div>
      </div>
      </div>

      <SelectCustomerModal
        isOpen={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        onSelect={(c) => {
          setCustomer(c);
          setCustomerPickerOpen(false);
        }}
        baseZIndex={100300}
      />

      <CustomerDetailsModal
        isOpen={customerDetailsOpen}
        onClose={() => {
          setCustomerDetailsOpen(false);
          setCustomerForDetails(null);
        }}
        customer={customerForDetails}
        onUpdate={() => {
          void load();
          onChanged?.();
        }}
        zIndex={100400}
      />
    </>
  );

  return createPortal(modalContent, document.body);
}
